import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';

// Foto real de Francisco González Trejo
const VET_IMAGE = '/assets/fran-veterinario.webp';
const CALENDLY_VALORACION = 'https://calendly.com/realimenta-naturalvet/cita';
const DOMAIN = 'https://www.realimentanaturalvet.com';

// Schema Person JSON-LD
const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Francisco González Trejo",
  "givenName": "Francisco",
  "familyName": "González Trejo",
  "jobTitle": "Veterinario especialista en nutrición natural",
  "description": "Veterinario colegiado Málaga nº 1397 especializado en nutrición natural individualizada para perros y gatos, sanos y enfermos.",
  "image": `${DOMAIN}/assets/fran-veterinario.webp`,
  "url": `${DOMAIN}/sobre-realimenta`,
  "sameAs": [],
  "alumniOf": {
    "@type": "CollegeOrUniversity",
    "name": "Universidad de Córdoba",
    "department": "Facultad de Veterinaria"
  },
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "degree",
      "name": "Licenciado en Veterinaria",
      "recognizedBy": {
        "@type": "CollegeOrUniversity",
        "name": "Universidad de Córdoba"
      },
      "dateCreated": "2010"
    },
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "certification",
      "name": "Máster REAL Animal"
    },
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "Colegiado nº 1397",
      "recognizedBy": {
        "@type": "Organization",
        "name": "Colegio Oficial de Veterinarios de Málaga"
      }
    }
  ],
  "workLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Málaga",
      "addressRegion": "Andalucía",
      "addressCountry": "ES"
    }
  },
  "worksFor": {
    "@type": "Organization",
    "name": "REALimenta",
    "url": DOMAIN
  }
};

export const SobrePage = () => {
  // Inject Person schema JSON-LD
  useEffect(() => {
    const existingScript = document.getElementById('person-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = 'person-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(personSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('person-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <>
      <SEOHead
        title="Francisco González Trejo | Veterinario nutricionista para perros y gatos | REALimenta"
        description="Veterinario colegiado Málaga nº 1397 especializado en nutrición natural individualizada para perros y gatos, sanos y enfermos. Consulta online en toda España."
        canonical="/sobre-realimenta"
      />

      <section className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[{ label: 'Sobre REALimenta' }]} />

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-12">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* H1 SEO optimizado */}
              <h1 
                className="font-manrope text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1A2E22] tracking-tighter mb-6"
                data-testid="sobre-h1"
              >
                Francisco González Trejo, veterinario especialista en{' '}
                <span 
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #3D6B4F 0%, #8AAE7F 100%)' }}
                >
                  nutrición natural
                </span>
                {' '}para perros y gatos
              </h1>

              {/* Párrafo 1: Presentación profesional */}
              <p className="text-text-secondary text-lg mb-5 leading-relaxed">
                Soy Francisco González Trejo, veterinario colegiado (nº 1397 MA) y licenciado por la Universidad de Córdoba en 2010. Diseño planes de nutrición natural individualizada para perros y gatos, sanos y enfermos, con consulta online en toda España.
              </p>

              {/* Párrafo 2: Origen de la vocación */}
              <p className="text-text-secondary text-lg mb-5 leading-relaxed">
                Durante mis años en la clínica veterinaria me di cuenta de algo que lo cambió todo: la base de la salud está en lo que se come. Lo comprobé también en casa, cuidando mi propia alimentación y la de mi familia. Entendí que no bastaba con tratar síntomas sin mirar el plato, así que me formé a fondo en nutrición animal (Máster REAL Animal).
              </p>

              {/* Párrafo 3: Filosofía de REALimenta */}
              <p className="text-text-secondary text-lg mb-5 leading-relaxed">
                Hoy dedico mi trabajo a ayudarte a alimentar a tu perro o a tu gato con criterio, de forma natural y adaptada a él. De ahí nace REALimenta: la convicción de que una alimentación de calidad puede transformar la vida de nuestros animales. No hablo de dietas milagro ni de modas pasajeras, sino de nutrición basada en alimentos reales, adaptada a cada individuo y respaldada por conocimiento actualizado.
              </p>

              {/* Párrafo 4: Enfoque práctico */}
              <p className="text-text-secondary text-lg mb-5 leading-relaxed">
                Mi enfoque es sencillo: escuchar, analizar y diseñar planes que funcionen en la práctica. Porque de nada sirve un plan perfecto sobre el papel si no se puede implementar en tu día a día.
              </p>

              {/* Párrafo 5: Modalidad online */}
              <p className="text-text-secondary text-lg leading-relaxed">
                Trabajo de forma online desde Málaga, atendiendo a familias de toda España. La distancia no es una barrera cuando hay comunicación, seguimiento y compromiso por ambas partes.
              </p>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-brand-lg">
                <img
                  src={VET_IMAGE}
                  alt="Francisco González Trejo - Veterinario especialista en nutrición natural para perros y gatos"
                  className="w-full h-auto aspect-[4/3] object-cover"
                  loading="lazy"
                />
                {/* Caption - positioned bottom-right */}
                <div className="absolute bottom-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-4 pt-8 pr-4 pl-16 rounded-bl-3xl">
                  <p className="text-white font-medium text-right">
                    Francisco González Trejo
                  </p>
                  <p className="text-white/80 text-sm text-right">
                    Colegiado: 1397-MA
                  </p>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 md:left-10 bg-white rounded-2xl shadow-brand p-4">
                <p className="font-caveat text-brand-primary text-lg">Base en Málaga</p>
                <p className="text-text-muted text-sm">Atención online en toda España</p>
              </div>
            </motion.div>
          </div>

          {/* CTA Button after text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <a
              href={CALENDLY_VALORACION}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="sobre-cta-top"
            >
              Reserva tu valoración nutricional inicial
              <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="text-text-muted text-sm mt-3">
              35 €, descontables si después contratas el pack nutricional específico.
            </p>
          </motion.div>

          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-brand-sage/20 rounded-3xl p-8 md:p-12 mb-20 text-center max-w-4xl mx-auto"
          >
            <h2 className="font-manrope text-2xl md:text-3xl font-bold text-text-primary mb-6">
              Mi misión
            </h2>
            <p className="text-text-secondary text-lg leading-relaxed">
              Ayudar a que más perros y gatos disfruten de una alimentación que les nutra de verdad, 
              con familias que entienden qué necesitan sus compañeros y cómo dárselo de forma práctica y sostenible.
            </p>
          </motion.div>

          {/* Final CTA Button */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <a
              href={CALENDLY_VALORACION}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="sobre-cta-bottom"
            >
              Reserva tu valoración nutricional inicial
              <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default SobrePage;
